import { useEffect, useState, useRef } from "react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { logError } from "../lib/log";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, Shield, ShieldOff, Users, UserPlus, Pencil, Trash2, Calculator, Building2, Database, Filter, Check, X, Mailbox, Key, Copy, RefreshCw, Trash, Terminal, Eye, EyeOff } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import CostMatrixPanel from "./admin/CostMatrixPanel";
import LifecyclePanel from "./admin/LifecyclePanel";
import ClassificationPanel from "./admin/ClassificationPanel";
import DataSourcesPanel from "./admin/DataSourcesPanel";

function MisPanel() {
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/mis/upload-status");
      setStatus(data);
    } catch (e) { logError("loadStatus failed", e); }
  };
  useEffect(() => { loadStatus(); }, []);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx") && !file.name.toLowerCase().endsWith(".xlsm")) {
      toast.error("Upload an .xlsx file");
      return;
    }
    setUploading(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/mis/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      const mirrorOK = data.mis_analysis?.ok;
      toast.success(
        `MIS refreshed · ${data.snapshot.latest_month} · ${data.snapshot.operational_properties} props · ${
          mirrorOK ? "BD + MIS personas synced" : "BD synced (MIS mirror failed — see status panel)"
        }`
      );
      await loadStatus();
    } catch (e) {
      toast.error(`Upload failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-white border border-brand-line p-6 rounded-lg" data-testid="upload-card">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">Upload latest MIS · syncs BD + MIS</div>
        <h2 className="font-display font-bold text-2xl mt-1 mb-4">Refresh MIS data</h2>
        <p className="text-sm text-zinc-700 leading-relaxed mb-6">
          Drop the latest <span className="font-mono-tech bg-brand-paper px-1.5 py-0.5 rounded">MIS.xlsx</span> with <span className="font-bold">Revenue Data</span> and <span className="font-bold">Expenses Data</span> sheets. <span className="font-bold text-brand-ink">A single upload refreshes BOTH the BD persona's Dashboard and the MIS persona's Expense Tracker / Outliers / Property Compare.</span> Parser extracts FY 22-23 onwards. Previous file is auto-backed-up.
        </p>
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-brand-ink bg-brand-paper p-12 text-center hover:bg-brand-yellow/10 transition-colors cursor-pointer rounded-lg"
          data-testid="upload-dropzone"
        >
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="upload-input" />
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-10 h-10 animate-spin text-brand-ink" />
              <div className="font-display font-bold">Parsing MIS…</div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="w-14 h-14 bg-brand-yellow rounded-2xl flex items-center justify-center">
                <Upload className="w-6 h-6 text-brand-ink" />
              </div>
              <div className="font-display font-bold text-lg">Click to upload, or drop file here</div>
              <div className="font-mono-tech text-xs text-zinc-500 uppercase tracking-wide">.xlsx · MIS template only</div>
            </div>
          )}
        </div>
        {result && (
          <div className="mt-6 border-2 border-brand-teal bg-brand-teal/10 p-4 rounded-lg" data-testid="upload-result">
            <div className="flex items-center gap-2 font-display font-bold text-brand-teal">
              <CheckCircle2 className="w-4 h-4" /> Upload successful
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 font-mono-tech text-xs">
              <div><div className="text-zinc-500 uppercase tracking-wide">Latest month</div><div className="font-bold mt-0.5">{result.snapshot.latest_month}</div></div>
              <div><div className="text-zinc-500 uppercase tracking-wide">Properties</div><div className="font-bold mt-0.5">{result.snapshot.operational_properties}</div></div>
              <div><div className="text-zinc-500 uppercase tracking-wide">Beds</div><div className="font-bold mt-0.5">{result.snapshot.total_capacity_beds}</div></div>
              <div><div className="text-zinc-500 uppercase tracking-wide">Avg occupancy</div><div className="font-bold mt-0.5">{result.snapshot.avg_occupancy_pct}%</div></div>
            </div>
            {/* MIS Analysis mirror status — confirms one upload synced both persona dashboards */}
            <div className="mt-3 pt-3 border-t border-brand-teal/30 grid grid-cols-2 gap-3 text-[11px]">
              <div className="flex items-center gap-2">
                <span className={`inline-block w-2 h-2 rounded-full ${result.mis_analysis?.ok ? "bg-brand-teal" : "bg-rose-500"}`} />
                <span className="font-mono-tech uppercase tracking-wide text-zinc-500">BD persona</span>
                <span className="font-bold">refreshed</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-block w-2 h-2 rounded-full ${result.mis_analysis?.ok ? "bg-brand-teal" : "bg-rose-500"}`} />
                <span className="font-mono-tech uppercase tracking-wide text-zinc-500">MIS persona</span>
                <span className="font-bold">{result.mis_analysis?.ok ? `synced · ${result.mis_analysis.locations} locations` : `mirror failed: ${result.mis_analysis?.error || "unknown"}`}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-brand-line p-6 rounded-lg" data-testid="status-card">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">Current MIS</div>
        <h3 className="font-display font-bold text-lg mt-1 mb-4">Loaded data status</h3>
        {status?.file_present ? (
          <div className="space-y-4">
            <div><div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-400">Latest month</div><div className="font-display font-black text-2xl">{status.latest_month}</div></div>
            <div><div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-400">Operational properties</div><div className="font-display font-black text-2xl">{status.operational_properties}</div></div>
            <div><div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-400">Uploaded at</div><div className="font-mono-tech text-xs text-zinc-700 mt-1">{status.uploaded_at?.replace("T", " ").slice(0, 19)} UTC</div></div>
            <div><div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-400">File size</div><div className="font-mono-tech text-sm text-zinc-700 mt-1 flex items-center gap-1.5"><FileSpreadsheet className="w-3 h-3" /> {status.file_size_kb} KB</div></div>
          </div>
        ) : (
          <div className="flex items-start gap-2 text-sm text-zinc-600">
            <AlertCircle className="w-4 h-4 mt-0.5 text-orange-500" /><span>No MIS file uploaded yet.</span>
          </div>
        )}
      </div>
    </div>
  );
}

const ROLES = ["BD Manager", "Cluster Head", "Reviewer", "Approver", "Viewer"];

function AccessRequestsPanel({ onCountChange }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/access-requests");
      setRequests(data);
      onCountChange?.(data.length);
    } catch (e) {
      logError("access-requests load failed", e);
      toast.error("Failed to load access requests");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (req, verb) => {
    if (!window.confirm(`${verb === "approve" ? "Approve" : "Reject"} access for ${req.email}?`)) return;
    setBusyId(req.request_id);
    try {
      await api.post(`/admin/access-requests/${req.request_id}/${verb}`);
      toast.success(`${req.email} · ${verb}d`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${verb} failed`);
    } finally { setBusyId(null); }
  };

  return (
    <div className="bg-white border border-brand-line rounded-lg mb-6" data-testid="access-requests-panel">
      <div className="px-6 py-4 border-b border-brand-line flex items-center justify-between gap-3">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">Access queue</div>
          <h3 className="font-display font-bold text-lg mt-1 flex items-center gap-2">
            Access requests
            {requests.length > 0 && (
              <span className="font-mono-tech text-[10px] uppercase tracking-wide bg-brand-pink text-white px-2 py-0.5 rounded-full" data-testid="access-requests-badge">
                {requests.length} pending
              </span>
            )}
          </h3>
        </div>
        <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          First-time @gostops.com sign-ins land here
        </span>
      </div>
      {loading ? (
        <div className="p-6 text-center font-mono-tech text-xs text-zinc-500 uppercase tracking-wide">Loading…</div>
      ) : requests.length === 0 ? (
        <div className="p-6 flex items-center justify-center gap-2 text-sm text-zinc-500" data-testid="access-requests-empty">
          <Mailbox className="w-4 h-4" /> No pending requests
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-paper">
              <tr className="text-left">
                {["Requester", "Email", "Requested", "Actions"].map(h => (
                  <th key={h} className="px-6 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-line">
              {requests.map(r => (
                <tr key={r.request_id} className="hover:bg-brand-paper" data-testid={`access-request-row-${r.request_id}`}>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-3">
                      {r.picture ? (
                        <img src={r.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-brand-yellow flex items-center justify-center text-xs font-black">{r.name?.slice(0,1) || "?"}</div>
                      )}
                      <span className="font-display font-semibold">{r.name || "—"}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3 font-mono-tech text-xs">{r.email}</td>
                  <td className="px-6 py-3 font-mono-tech text-[11px] text-zinc-600">
                    {r.requested_at ? new Date(r.requested_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => act(r, "approve")}
                        disabled={busyId === r.request_id}
                        data-testid={`approve-access-${r.request_id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-brand-ink bg-brand-yellow hover:bg-brand-ink hover:text-brand-yellow transition-colors font-display font-bold text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Check className="w-3.5 h-3.5" /> Approve
                      </button>
                      <button
                        onClick={() => act(r, "reject")}
                        disabled={busyId === r.request_id}
                        data-testid={`reject-access-${r.request_id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-brand-line hover:bg-brand-pink/10 hover:border-brand-pink transition-colors font-display font-bold text-xs text-brand-pink disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <X className="w-3.5 h-3.5" /> Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UserDialog({ open, onOpenChange, initial, onSaved }) {
  const { user: currentUser } = useAuth();
  const isEdit = !!initial?.user_id;
  const [form, setForm] = useState({
    email: "", name: "", role: "BD Manager",
    property_ic_access: true, property_ic_approver: false,
    is_admin: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(isEdit ? {
        email: initial.email || "",
        name: initial.name || "",
        role: initial.role || "BD Manager",
        property_ic_access: initial.permissions?.property_ic_access ?? true,
        property_ic_approver: initial.permissions?.property_ic_approver ?? false,
        is_admin: !!initial.is_admin,
      } : { email: "", name: "", role: "BD Manager", property_ic_access: true, property_ic_approver: false, is_admin: false });
    }
  }, [open, initial, isEdit]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Show the admin toggle inside the Edit modal only for the primary admin,
  // and never for their own row or another primary admin.
  const canToggleAdmin = isEdit
    && currentUser?.is_primary_admin
    && initial?.user_id !== currentUser?.user_id
    && !initial?.is_primary_admin;
  const adminChanged = isEdit && form.is_admin !== !!initial?.is_admin;

  const submit = async () => {
    if (!form.email.trim() || !form.name.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { email: form.email, name: form.name, role: form.role };
        if (adminChanged) payload.is_admin = form.is_admin;
        await api.patch(`/admin/users/${initial.user_id}`, payload);
        toast.success(adminChanged ? `User updated · admin ${form.is_admin ? "granted" : "revoked"}` : "User updated");
      } else {
        await api.post("/admin/users", form);
        toast.success("User added");
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-lg max-w-md" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight text-xl">{isEdit ? "Edit user" : "Add new user"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="font-mono-tech text-[10px] uppercase tracking-[0.2em]">Full name</Label>
            <Input className="rounded-lg" value={form.name} onChange={e => set("name", e.target.value)} placeholder="Aman Sharma" data-testid="user-name-input" />
          </div>
          <div>
            <Label className="font-mono-tech text-[10px] uppercase tracking-[0.2em]">Email</Label>
            <Input className="rounded-lg" type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="aman@gostops.com" data-testid="user-email-input" />
          </div>
          <div>
            <Label className="font-mono-tech text-[10px] uppercase tracking-[0.2em]">Role</Label>
            <select
              className="w-full rounded-lg border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow"
              value={form.role}
              onChange={e => set("role", e.target.value)}
              data-testid="user-role-select"
            >
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {!isEdit && (
            <div className="grid grid-cols-2 gap-3 border-t border-brand-line pt-3">
              <label className="flex items-center justify-between bg-brand-paper rounded-lg p-3">
                <span className="text-xs font-display font-semibold">Property IC access</span>
                <Switch checked={form.property_ic_access} onCheckedChange={v => set("property_ic_access", v)} data-testid="user-access-switch" />
              </label>
              <label className="flex items-center justify-between bg-brand-paper rounded-lg p-3">
                <span className="text-xs font-display font-semibold">Final approver</span>
                <Switch checked={form.property_ic_approver} onCheckedChange={v => set("property_ic_approver", v)} data-testid="user-approver-switch" />
              </label>
            </div>
          )}
          {canToggleAdmin && (
            <div className="border-t border-brand-line pt-3">
              <label className="flex items-center justify-between bg-brand-yellow/15 border border-brand-yellow rounded-lg p-3" data-testid="edit-admin-toggle-row">
                <div>
                  <div className="text-xs font-display font-bold">Grant admin access</div>
                  <div className="text-[11px] text-zinc-600 mt-0.5">Admins can upload MIS, approve ICs, edit cost matrix &amp; toggle other users' permissions.</div>
                </div>
                <Switch checked={form.is_admin} onCheckedChange={v => set("is_admin", v)} data-testid="edit-admin-toggle" />
              </label>
            </div>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg" data-testid="user-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="rounded-lg bg-brand-ink text-brand-yellow font-display font-bold hover:bg-brand-yellow hover:text-brand-ink"
            data-testid="user-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDelete({ open, onOpenChange, user, onConfirmed }) {
  const [deleting, setDeleting] = useState(false);
  const submit = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${user.user_id}`);
      toast.success(`Removed ${user.email}`);
      onConfirmed();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-lg max-w-sm" data-testid="delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight text-lg">Remove this user?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-700 mt-2">
          <span className="font-display font-bold">{user?.name}</span> <span className="text-zinc-500">({user?.email})</span> will lose access immediately. Their active sessions will be revoked. This cannot be undone.
        </p>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg" data-testid="delete-cancel">Cancel</Button>
          <Button onClick={submit} disabled={deleting} className="rounded-lg bg-brand-pink text-white font-display font-bold hover:bg-brand-pink/90" data-testid="delete-confirm">
            {deleting ? "Removing…" : "Remove user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UsersPanel() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, user: null });
  const [confirm, setConfirm] = useState({ open: false, user: null });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch {
      toast.error("Failed to load users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (u, key) => {
    const next = !u.permissions[key];
    setUsers(prev => prev.map(x => x.user_id === u.user_id ? { ...x, permissions: { ...x.permissions, [key]: next } } : x));
    try {
      await api.patch(`/admin/users/${u.user_id}/permissions`, { [key]: next });
      toast.success(`${u.email} · ${key.replace(/_/g, " ")} ${next ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Update failed"); load();
    }
  };

  const toggleAdmin = async (u) => {
    if (!currentUser?.is_primary_admin) {
      toast.error(`Only the primary admin (${u.is_primary_admin ? u.email : "yash.shukla@gostops.com"}) can change admin status`);
      return;
    }
    const next = !u.is_admin;
    const verb = next ? "Promote to admin" : "Remove admin";
    if (!window.confirm(`${verb}: ${u.email}?\n\n${next ? "They will get admin access — MIS uploads, IC approvals, cost matrix, and permission toggles for other users. Only YOU (the primary admin) can grant this." : "They will lose admin access but keep their existing IC/MIS permissions."}`)) return;
    try {
      const { data } = await api.patch(`/admin/users/${u.user_id}`, { is_admin: next });
      setUsers(prev => prev.map(x => x.user_id === u.user_id ? data : x));
      toast.success(`${u.email} · admin ${next ? "granted" : "revoked"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Admin toggle failed");
    }
  };

  return (
    <>
      <div className="bg-white border border-brand-line rounded-lg" data-testid="users-panel">
        <div className="px-6 py-4 border-b border-brand-line flex items-center justify-between gap-3">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">Access control</div>
            <h3 className="font-display font-bold text-lg mt-1">Team & permissions</h3>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">{users.length} users</span>
            <button
              onClick={() => setDialog({ open: true, user: null })}
              data-testid="add-user-btn"
              className="flex items-center gap-2 bg-brand-yellow text-brand-ink font-display font-bold text-sm px-4 py-2 rounded-lg border-2 border-brand-ink hover:bg-brand-ink hover:text-brand-yellow transition-colors"
            >
              <UserPlus className="w-4 h-4" /> Add user
            </button>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center font-mono-tech text-xs text-zinc-500 uppercase tracking-wide">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-paper">
                <tr className="text-left">
                  {["User", "Email", "Role", "IC access", "Final approver", "Actions"].map(h => (
                    <th key={h} className="px-6 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-line">
                {users.map(u => (
                  <tr key={u.user_id} className="hover:bg-brand-paper" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-brand-yellow flex items-center justify-center text-xs font-black">{u.name?.slice(0,1) || "U"}</div>
                        )}
                        <span className="font-display font-semibold">{u.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3 font-mono-tech text-xs">{u.email}</td>
                    <td className="px-6 py-3">
                      {u.is_admin ? (
                        <span className="font-mono-tech text-[10px] uppercase tracking-wide bg-brand-purple/20 text-brand-purple px-2 py-1 rounded">ADMIN</span>
                      ) : (
                        <span className="font-mono-tech text-[10px] uppercase tracking-wide bg-zinc-100 text-zinc-600 px-2 py-1 rounded">{(u.role || "BD Manager").toUpperCase()}</span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <Switch
                        checked={!!u.permissions?.property_ic_access}
                        onCheckedChange={() => toggle(u, "property_ic_access")}
                        disabled={u.is_admin}
                        data-testid={`toggle-access-${u.user_id}`}
                      />
                    </td>
                    <td className="px-6 py-3">
                      <Switch
                        checked={!!u.permissions?.property_ic_approver}
                        onCheckedChange={() => toggle(u, "property_ic_approver")}
                        data-testid={`toggle-approver-${u.user_id}`}
                      />
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleAdmin(u)}
                          disabled={u.user_id === currentUser?.user_id || u.is_primary_admin || !currentUser?.is_primary_admin}
                          data-testid={`toggle-admin-${u.user_id}`}
                          className={`p-1.5 rounded-md border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                            u.is_admin
                              ? "border-brand-purple/40 bg-brand-purple/10 hover:bg-brand-purple/20"
                              : "border-brand-line hover:bg-brand-yellow hover:border-brand-ink"
                          }`}
                          title={
                            u.is_primary_admin
                              ? "Primary admin — cannot be demoted (edit PRIMARY_ADMIN_EMAIL in .env)"
                              : u.user_id === currentUser?.user_id
                              ? "You can't change your own admin status"
                              : !currentUser?.is_primary_admin
                              ? "Only the primary admin can promote/demote"
                              : (u.is_admin ? "Revoke admin" : "Promote to admin")
                          }
                        >
                          {u.is_admin
                            ? <ShieldOff className="w-3.5 h-3.5 text-brand-purple" />
                            : <Shield className="w-3.5 h-3.5 text-zinc-700" />}
                        </button>
                        <button
                          onClick={() => setDialog({ open: true, user: u })}
                          data-testid={`edit-user-${u.user_id}`}
                          className="p-1.5 rounded-md border border-brand-line hover:bg-brand-yellow hover:border-brand-ink transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5 text-zinc-700" />
                        </button>
                        <button
                          onClick={() => setConfirm({ open: true, user: u })}
                          disabled={u.is_primary_admin || u.user_id === currentUser?.user_id || (u.is_admin && !currentUser?.is_primary_admin)}
                          data-testid={`delete-user-${u.user_id}`}
                          className="p-1.5 rounded-md border border-brand-line hover:bg-brand-pink/10 hover:border-brand-pink disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title={
                            u.is_primary_admin
                              ? "Primary admin — cannot be deleted"
                              : u.user_id === currentUser?.user_id
                              ? "You can't delete yourself"
                              : (u.is_admin && !currentUser?.is_primary_admin)
                              ? "Only the primary admin can delete an admin user"
                              : "Delete"
                          }
                        >
                          <Trash2 className="w-3.5 h-3.5 text-brand-pink" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!users.length && (
                  <tr><td colSpan={6} className="px-6 py-12 text-center font-mono-tech text-xs text-zinc-500 uppercase tracking-wide">No users yet — add one to get started</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <UserDialog open={dialog.open} onOpenChange={(v) => setDialog({ ...dialog, open: v })} initial={dialog.user} onSaved={load} />
      {confirm.user && <ConfirmDelete open={confirm.open} onOpenChange={(v) => setConfirm({ ...confirm, open: v })} user={confirm.user} onConfirmed={load} />}
    </>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("users");

  if (loading) {
    return (
      <AppShell title="Admin" subtitle="Loading…">
        <div className="font-mono-tech text-zinc-500 text-sm">Verifying access</div>
      </AppShell>
    );
  }
  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;

  // Sub-tabs under "Property Settings" — both deal with how individual properties
  // are categorized (operational/non-operational head-by-head, and lifecycle dates).
  const PROPERTY_SETTINGS_KEYS = ["classification", "lifecycle"];
  const isPropertySettings = PROPERTY_SETTINGS_KEYS.includes(tab);

  const tabs = [
    { key: "users", label: "Access Control", icon: Users, color: "#9370DB" },
    { key: "cost-matrix", label: "Cost Matrix", icon: Calculator, color: "#20B2AA" },
    { key: "mis", label: "MIS Upload", icon: Shield, color: "#FF69B4" },
    { key: "data-sources", label: "Data Sources", icon: Database, color: "#FB7185" },
    { key: "property-settings", label: "Property Settings", icon: Building2, color: "#6366F1" },
    { key: "api", label: "API", icon: Terminal, color: "#0EA5E9" },
  ];

  const propertySubTabs = [
    { key: "classification", label: "Op / Non-Op heads", icon: Filter },
    { key: "lifecycle", label: "Lifecycle dates", icon: Building2 },
  ];

  // First click on the parent tab → land on Op/Non-Op (the more frequently used one).
  const onTabClick = (key) => {
    if (key === "property-settings") {
      setTab(isPropertySettings ? tab : "classification");
    } else {
      setTab(key);
    }
  };

  return (
    <AppShell title="Admin" subtitle="Restricted · admin only">
      <div className="flex gap-2 mb-6 border-b border-brand-line" data-testid="admin-tabs">
        {tabs.map(t => {
          const active = tab === t.key || (t.key === "property-settings" && isPropertySettings);
          return (
            <button
              key={t.key}
              onClick={() => onTabClick(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-3 font-display font-bold text-sm tracking-tight border-b-2 -mb-px transition-colors ${
                active ? "border-brand-ink text-brand-ink" : "border-transparent text-zinc-400 hover:text-zinc-700"
              }`}
              style={active ? { borderColor: t.color, color: t.color } : {}}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Sub-nav for Property Settings — only renders when the parent tab is active. */}
      {isPropertySettings && (
        <div className="flex gap-1.5 mb-5 -mt-2" data-testid="admin-subtabs">
          {propertySubTabs.map(s => (
            <button
              key={s.key}
              onClick={() => setTab(s.key)}
              data-testid={`admin-subtab-${s.key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono-tech uppercase tracking-wider transition-colors ${
                tab === s.key
                  ? "bg-brand-ink text-brand-paper"
                  : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
              }`}
            >
              <s.icon className="w-3 h-3" /> {s.label}
            </button>
          ))}
        </div>
      )}

      {tab === "users" && (
        <>
          <AccessRequestsPanel />
          <UsersPanel />
        </>
      )}
      {tab === "cost-matrix" && <CostMatrixPanel />}
      {tab === "mis" && <MisPanel />}
      {tab === "data-sources" && <DataSourcesPanel />}
      {tab === "classification" && <ClassificationPanel />}
      {tab === "lifecycle" && <LifecyclePanel />}
      {tab === "api" && <ApiPanel />}
    </AppShell>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Read-only external data access layer — connection details, API key mgmt,
// access log. Admin-only.
// ──────────────────────────────────────────────────────────────────────────────
function ApiPanel() {
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatedKey, setGeneratedKey] = useState(null); // { raw_key, prefix, created_at, action }
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [log, setLog] = useState([]);
  const [logFilterStatus, setLogFilterStatus] = useState("");
  const [logFilterEndpoint, setLogFilterEndpoint] = useState("");
  const [logEndpoints, setLogEndpoints] = useState([]);
  const [busy, setBusy] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const restBase = `${backendUrl}/api/external`;
  // MCP is mounted under /api so Emergent's ingress (which only forwards /api/*
  // to the backend) can reach it. Direct backend hosts can also use /mcp/external.
  const mcpUrl = `${backendUrl}/api/mcp/external`;

  const loadKey = async () => {
    try {
      const { data } = await api.get("/admin/external-keys");
      setActive(data.active || null);
    } catch (e) { logError("load external key failed", e); }
  };

  const loadLog = async () => {
    const params = {};
    if (logFilterStatus) params.status_code = logFilterStatus;
    if (logFilterEndpoint) params.endpoint = logFilterEndpoint;
    try {
      const { data } = await api.get("/admin/external-keys/access-log", { params });
      setLog(data.items || []);
      setLogEndpoints(data.endpoints || []);
    } catch (e) { logError("load access log failed", e); }
  };

  useEffect(() => {
    (async () => { setLoading(true); await Promise.all([loadKey(), loadLog()]); setLoading(false); })();
  }, []);

  useEffect(() => { loadLog(); /* eslint-disable-next-line */ }, [logFilterStatus, logFilterEndpoint]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  };

  const doGenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/external-keys");
      setGeneratedKey({ ...data, action: "generated" });
      await loadKey();
      toast.success("API key generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generate failed");
    } finally { setBusy(false); }
  };

  const doRotate = async () => {
    if (!window.confirm("Rotate the API key?\n\nThe current key will stop working immediately. Any agent using it must be updated with the new key.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/external-keys/rotate");
      setGeneratedKey({ ...data, action: "rotated" });
      await loadKey();
      toast.success("API key rotated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rotate failed");
    } finally { setBusy(false); }
  };

  const doRevoke = async () => {
    setBusy(true);
    try {
      await api.post("/admin/external-keys/revoke");
      await loadKey();
      setRevokeOpen(false);
      toast.success("API key revoked");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="api-panel">
      {/* ─── Connection details ─────────────────────────────────────────── */}
      <div className="bg-white border border-brand-line rounded-lg" data-testid="api-connection-card">
        <div className="px-6 py-4 border-b border-brand-line">
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">Read-only data access</div>
          <h3 className="font-display font-bold text-lg mt-1">Connection details</h3>
          <div className="font-mono-tech text-xs text-zinc-600 mt-1">
            Two parallel surfaces, same Bearer-token auth. Non-GET requests to <span className="font-bold">/api/external/*</span> return 405.
          </div>
        </div>
        <div className="p-6 space-y-4">
          <ConnectionRow label="REST base URL" value={restBase} testid="conn-rest" onCopy={() => copyToClipboard(restBase, "REST URL")} />
          <ConnectionRow label="MCP endpoint" value={mcpUrl} testid="conn-mcp" onCopy={() => copyToClipboard(mcpUrl, "MCP URL")} />
          <div className="flex items-center gap-4 text-xs font-mono-tech">
            <span className="text-zinc-500 uppercase tracking-wide">api_version</span>
            <span className="font-bold bg-brand-paper px-2 py-0.5 rounded border border-brand-line" data-testid="conn-api-version">1.0</span>
            <span className="text-zinc-500 uppercase tracking-wide ml-4">Auth</span>
            <span className="font-bold">Authorization: Bearer &lt;key&gt;</span>
          </div>
        </div>
      </div>

      {/* ─── Key management ─────────────────────────────────────────────── */}
      <div className="bg-white border border-brand-line rounded-lg" data-testid="api-key-card">
        <div className="px-6 py-4 border-b border-brand-line flex items-center justify-between gap-3">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">Auth key</div>
            <h3 className="font-display font-bold text-lg mt-1">API key management</h3>
          </div>
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">bcrypt-hashed · one active key at a time</span>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="font-mono-tech text-xs text-zinc-500 uppercase tracking-wide">Loading…</div>
          ) : !active ? (
            <div className="flex items-center justify-between gap-4 bg-brand-paper border border-dashed border-brand-line rounded-lg p-5" data-testid="api-key-empty">
              <div>
                <div className="font-display font-bold text-sm">No active API key</div>
                <div className="text-xs text-zinc-600 mt-0.5">Generate one to allow a founder-level AI agent to read BD Engine data.</div>
              </div>
              <button
                onClick={doGenerate}
                disabled={busy}
                data-testid="generate-key-btn"
                className="flex items-center gap-2 bg-brand-yellow text-brand-ink font-display font-bold text-sm px-4 py-2 rounded-lg border-2 border-brand-ink hover:bg-brand-ink hover:text-brand-yellow transition-colors disabled:opacity-50"
              >
                <Key className="w-4 h-4" /> Generate key
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-4 border border-brand-line rounded-lg p-5" data-testid="api-key-active">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-brand-yellow/20 border border-brand-yellow flex items-center justify-center">
                  <Key className="w-5 h-5 text-brand-ink" />
                </div>
                <div>
                  <div className="font-display font-bold text-sm" data-testid="api-key-label">{active.label}</div>
                  <div className="font-mono-tech text-xs text-zinc-600 mt-0.5">
                    <span className="bg-brand-paper px-2 py-0.5 rounded border border-brand-line" data-testid="api-key-prefix">{active.prefix}•••••••••</span>
                    <span className="ml-3 text-zinc-500">created {active.created_at?.replace("T", " ").slice(0, 19)} UTC</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={doRotate}
                  disabled={busy}
                  data-testid="rotate-key-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-brand-ink bg-brand-yellow hover:bg-brand-ink hover:text-brand-yellow transition-colors font-display font-bold text-xs disabled:opacity-50"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Rotate
                </button>
                <button
                  onClick={() => setRevokeOpen(true)}
                  disabled={busy}
                  data-testid="revoke-key-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-brand-line hover:bg-brand-pink/10 hover:border-brand-pink transition-colors font-display font-bold text-xs text-brand-pink disabled:opacity-50"
                >
                  <Trash className="w-3.5 h-3.5" /> Revoke
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── Access log ─────────────────────────────────────────────────── */}
      <div className="bg-white border border-brand-line rounded-lg" data-testid="api-log-card">
        <div className="px-6 py-4 border-b border-brand-line flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">Audit trail</div>
            <h3 className="font-display font-bold text-lg mt-1">Access log <span className="font-mono-tech text-[11px] font-normal text-zinc-500 ml-2">latest 1000 requests</span></h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={logFilterStatus}
              onChange={e => setLogFilterStatus(e.target.value)}
              data-testid="log-filter-status"
              className="rounded-md border border-brand-line bg-white px-3 py-1.5 text-xs font-mono-tech focus:outline-none focus:ring-2 focus:ring-brand-yellow"
            >
              <option value="">All status codes</option>
              <option value="200">200 OK</option>
              <option value="401">401 Unauthorized</option>
              <option value="404">404 Not Found</option>
              <option value="405">405 Method Not Allowed</option>
              <option value="500">500 Server Error</option>
            </select>
            <select
              value={logFilterEndpoint}
              onChange={e => setLogFilterEndpoint(e.target.value)}
              data-testid="log-filter-endpoint"
              className="rounded-md border border-brand-line bg-white px-3 py-1.5 text-xs font-mono-tech focus:outline-none focus:ring-2 focus:ring-brand-yellow"
            >
              <option value="">All endpoints</option>
              {logEndpoints.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            <button
              onClick={loadLog}
              data-testid="log-refresh"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-brand-line hover:bg-brand-yellow hover:border-brand-ink transition-colors text-xs font-mono-tech"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-paper">
              <tr className="text-left">
                {["Time (UTC)", "Method", "Endpoint", "Status", "Latency", "IP", "User-Agent"].map(h => (
                  <th key={h} className="px-6 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-line">
              {log.map((r, idx) => (
                <tr key={idx} className="hover:bg-brand-paper" data-testid={`log-row-${idx}`}>
                  <td className="px-6 py-2 font-mono-tech text-[11px]">{r.timestamp?.replace("T", " ").slice(0, 19)}</td>
                  <td className="px-6 py-2 font-mono-tech text-[11px]">{r.method}</td>
                  <td className="px-6 py-2 font-mono-tech text-[11px] text-brand-ink">{r.endpoint}</td>
                  <td className="px-6 py-2">
                    <span className={`font-mono-tech text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${
                      r.status_code >= 200 && r.status_code < 300 ? "bg-emerald-100 text-emerald-800"
                      : r.status_code >= 400 && r.status_code < 500 ? "bg-orange-100 text-orange-800"
                      : "bg-rose-100 text-rose-800"
                    }`}>{r.status_code}</span>
                  </td>
                  <td className="px-6 py-2 font-mono-tech text-[11px]">{r.response_time_ms}ms</td>
                  <td className="px-6 py-2 font-mono-tech text-[11px] text-zinc-600">{r.ip || "—"}</td>
                  <td className="px-6 py-2 font-mono-tech text-[11px] text-zinc-500 truncate max-w-[260px]" title={r.user_agent}>{r.user_agent || "—"}</td>
                </tr>
              ))}
              {!log.length && (
                <tr><td colSpan={7} className="px-6 py-12 text-center font-mono-tech text-xs text-zinc-500 uppercase tracking-wide" data-testid="log-empty">
                  No requests yet
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: raw key shown ONCE */}
      <RawKeyDialog data={generatedKey} onClose={() => setGeneratedKey(null)} copy={copyToClipboard} />
      {/* Confirm revoke */}
      <Dialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <DialogContent className="rounded-lg max-w-sm" data-testid="revoke-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black tracking-tight text-lg">Revoke this API key?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-700 mt-2">
            The current key will stop working <span className="font-bold">immediately</span>. Any external agent using it will start returning 401. You will need to generate a new key to restore access.
          </p>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setRevokeOpen(false)} className="rounded-lg" data-testid="revoke-cancel">Cancel</Button>
            <Button onClick={doRevoke} disabled={busy} className="rounded-lg bg-brand-pink text-white font-display font-bold hover:bg-brand-pink/90" data-testid="revoke-confirm">
              {busy ? "Revoking…" : "Revoke key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConnectionRow({ label, value, testid, onCopy }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500 w-28 shrink-0">{label}</div>
      <code className="flex-1 font-mono-tech text-xs bg-brand-paper border border-brand-line rounded px-3 py-2 text-brand-ink overflow-x-auto whitespace-nowrap">{value}</code>
      <button
        onClick={onCopy}
        data-testid={`${testid}-copy`}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-brand-line hover:bg-brand-yellow hover:border-brand-ink transition-colors text-xs font-mono-tech"
      >
        <Copy className="w-3 h-3" /> Copy
      </button>
    </div>
  );
}

function RawKeyDialog({ data, onClose, copy }) {
  const [reveal, setReveal] = useState(true);
  useEffect(() => { if (data) setReveal(true); }, [data]);
  if (!data) return null;
  const masked = "•".repeat(Math.min(40, (data.raw_key || "").length));
  return (
    <Dialog open={!!data} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-lg max-w-xl" data-testid="raw-key-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight text-xl">
            API key {data.action === "rotated" ? "rotated" : "generated"}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-3 space-y-4">
          <div className="flex items-start gap-2 bg-brand-yellow/15 border border-brand-yellow p-3 rounded-lg">
            <AlertCircle className="w-4 h-4 mt-0.5 text-brand-ink shrink-0" />
            <div className="text-xs text-brand-ink">
              <span className="font-display font-bold">Copy and store this key now.</span> It will NOT be shown again. If you lose it, rotate to issue a new one.
            </div>
          </div>
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Raw key</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono-tech text-xs bg-brand-paper border border-brand-line rounded px-3 py-2 text-brand-ink break-all" data-testid="raw-key-value">
                {reveal ? data.raw_key : masked}
              </code>
              <button
                onClick={() => setReveal(r => !r)}
                data-testid="raw-key-toggle"
                className="p-2 rounded-md border border-brand-line hover:bg-brand-yellow hover:border-brand-ink transition-colors"
                title={reveal ? "Hide" : "Reveal"}
              >
                {reveal ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => copy(data.raw_key, "Raw API key")}
                data-testid="raw-key-copy"
                className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-brand-ink bg-brand-yellow hover:bg-brand-ink hover:text-brand-yellow transition-colors text-xs font-display font-bold"
              >
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs font-mono-tech">
            <div><div className="text-zinc-500 uppercase tracking-wide">Prefix</div><div className="font-bold mt-0.5">{data.prefix}•••••••••</div></div>
            <div><div className="text-zinc-500 uppercase tracking-wide">Created</div><div className="font-bold mt-0.5">{data.created_at?.replace("T", " ").slice(0, 19)} UTC</div></div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button onClick={onClose} className="rounded-lg bg-brand-ink text-brand-yellow font-display font-bold hover:bg-brand-yellow hover:text-brand-ink" data-testid="raw-key-close">
            I've saved the key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
